import { fromEvent, merge, timer, map, take, startWith } from 'rxjs';

/**
 * Scrolls to the specified anchor element and returns an observable that emits a boolean value.
 * The observable emits `true` immediately and then emits `false` after the specified duration.
 *
 * @param anchorElm - The anchor element to scroll to.
 * @param options - Optional parameters for scrolling behavior.
 * @param options.duration - The duration in milliseconds after which the observable emits `false`. Default is 3000ms.
 * @param options.scroll - Specifies whether to scroll to the anchor element. Default is `true`.
 * @param options.containerElm - The container element to scroll. If specified, the container element will be scrolled by -50 pixels. Default is `undefined`.
 * @returns An observable that emits `true` immediately and then emits `false` after the specified duration.
 */
export const fromErrorScroll = (
  anchorElm: HTMLElement,
  {
    scroll = true,
    containerElm,
    duration = 3000,
  }: { duration?: number; scroll?: boolean; containerElm?: HTMLElement } = {},
) => {
  if (scroll) {
    anchorElm.scrollIntoView();
    containerElm?.scrollBy({ top: -50 });
  }

  const click$ = fromEvent(anchorElm, 'click');

  return merge(click$, timer(duration)).pipe(
    map(() => false),
    take(1),
    startWith(true),
  );
};
